
import React, { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { X, Send, Upload, Loader2 } from "lucide-react";
import { format } from "date-fns";

export default function SubmitExcusalForm({ assignment, studentEmail, onClose, onSuccess }) {
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();

  const submitExcusalMutation = useMutation({
    mutationFn: async () => {
      if (!file) {
        throw new Error("Please upload proof of completion");
      }

      setUploading(true);

      // Upload file first
      const { file_url } = await base44.integrations.Core.UploadFile({ file });

      // Create excusal submission
      await base44.entities.ExcusalSubmission.create({
        other_id: assignment.other_id,
        student_email: studentEmail,
        student_name: `${assignment.student_first_name} ${assignment.student_last_name}`,
        teacher_name: assignment.teacher,
        teacher_email: assignment.teacher_email,
        subject: assignment.course_short_description || assignment.course,
        submission_date: format(new Date(), 'yyyy-MM-dd'),
        reason: reason,
        roster_entry_id: assignment.id,
        status: 'pending',
        proof_of_completion_url: file_url
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['myAssignments']);
      queryClient.invalidateQueries(['myExcusals']);
      setUploading(false);
      onSuccess();
    },
    onError: (error) => {
      setUploading(false);
      alert(error.message || "Failed to submit excusal request");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (reason.trim() && file) {
      submitExcusalMutation.mutate();
    } else {
      alert("Please provide a reason and upload proof of completion");
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.type;
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      
      if (!validTypes.includes(fileType)) {
        alert("Please upload a JPG, PNG, or PDF file");
        e.target.value = '';
        return;
      }
      
      setFile(selectedFile);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle>Submit Excusal Request</CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} disabled={submitExcusalMutation.isPending}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <Alert className="bg-blue-50 border-blue-200">
            <AlertDescription className="text-sm text-blue-900">
              <strong>Assignment:</strong> {assignment.course_short_description || assignment.course}
              <br />
              <strong>Teacher:</strong> {assignment.teacher}
              <br />
              <strong>Current Grade:</strong> <span className="font-semibold text-red-600">{assignment.athletic_grade || assignment.assignment_grade}</span>
            </AlertDescription>
          </Alert>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="block text-sm font-medium mb-2">
                Reason for Excusal Request <span className="text-red-600">*</span>
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain why you need an excusal for this assignment..."
                className="h-32"
                required
              />
            </div>

            <div>
              <Label className="block text-sm font-medium mb-2">
                Upload Proof of Completion <span className="text-red-600">*</span>
              </Label>
              <Alert className="mb-3 bg-orange-50 border-orange-200">
                <AlertDescription className="text-xs text-orange-900">
                  <strong>Required:</strong> You must upload proof that you completed the assignment (JPG, PNG, or PDF only)
                </AlertDescription>
              </Alert>
              <div className="flex items-center gap-3">
                <Input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={handleFileChange}
                  className="flex-1"
                  required
                />
                <Upload className="w-5 h-5 text-slate-400" />
              </div>
              {file && (
                <p className="text-sm text-green-600 mt-2">
                  ✓ Selected: {file.name}
                </p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-red-700 hover:bg-red-800"
              disabled={!reason.trim() || !file || submitExcusalMutation.isPending || uploading}
            >
              {uploading || submitExcusalMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading & Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Excusal Request
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
